import { useAppSelector } from 'src/store';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { object } from 'yup';
import Input from 'src/components/Input/Input';
import { useParams } from 'react-router-dom';
import {
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useUpdateIntegrationMutation,
} from 'src/services';
import { Skeleton } from 'src/components/ui/skeleton';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { useNotification } from 'src/hooks/useNotification';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const ZendeskChatStep3 = ({
  onClick = () => {},
  onClose = () => {},
}: {
  onClick?: (integration?: SelectedIntegration) => void;
  onClose?: (isFinal?: boolean) => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const isLastStep = selectedIntegration.setupSteps === 3;
  const [deployedSettings, setDeployedSettings] = useState(null);
  const { data: configurationSettings, isLoading: isLoadingConfigurationSettings } =
    useGetAllConfigurationSettingsQuery({ id }, { skip: !id });
  const { data: configurationSettingsPrompt } = useGetConfigurationSettingsPromptQuery(
    {
      project_id: id,
      settings_id: deployedSettings?.id,
    },
    {
      skip: !(id && deployedSettings),
    },
  );
  const [
    updateIntegration,
    { data: updateIntegrationData, isLoading: isLoadingUpdateIntegration },
  ] = useUpdateIntegrationMutation();
  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      author_display_name: selectedIntegration?.integration_data?.author_display_name || '',
      author_avatar_url: selectedIntegration?.integration_data?.author_avatar_url || '',
      welcome_message: selectedIntegration?.integration_data?.welcome_message || '',
    },
    resolver: yupResolver(
      object({
        author_display_name: stringScheme({ required: true }).label('Author display name'),
        author_avatar_url: stringScheme({ required: false }).label('Author avatar url').nullable(),
        welcome_message: stringScheme({ required: true }).label('AI Welcome message'),
      }),
    ),
  });

  useEffect(() => {
    if (configurationSettings) {
      const deployed = configurationSettings.items.find((setting) => setting.is_deployed);

      if (deployed) {
        setDeployedSettings(deployed);
      }
    }
  }, [configurationSettings]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const name = configurationSettingsPrompt?.input_data?.pf_ai_name;

      if (typeof name === 'string' && name) {
        setValue('author_display_name', name);
      }
    }
  }, [configurationSettingsPrompt]);

  useLoaderWhile(isLoadingUpdateIntegration);

  useEffect(() => {
    if (updateIntegrationData) {
      isLastStep ? onClose() : onClick({ ...selectedIntegration, ...updateIntegration });
    }
  }, [updateIntegrationData]);

  const submit = (formData: {
    author_display_name: string;
    author_avatar_url: string;
    welcome_message: string;
  }) => {
    const request = {
      status: selectedIntegration.status,
      integration_type: selectedIntegration.integration_type,
      integration_data: {
        ...selectedIntegration.integration_data,
        author_display_name: formData.author_display_name,
        author_avatar_url: formData.author_avatar_url || null,
        welcome_message: formData.welcome_message,
      },
    };

    updateIntegration({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: request,
    }).then((updateIntegrationResult) => {
      if (!('error' in updateIntegrationResult)) {
        notification.success('Integration Updated.');
      }
    });
  };

  return (
    <LoaderWrapper
      loader={<Skeleton className="w-full h-16" />}
      isLoading={isLoadingConfigurationSettings || isLoadingUpdateIntegration}
    >
      <form id="zendesk-chat-step-3-form" onSubmit={handleSubmit(submit)}>
        <div>
          <Controller
            control={control}
            name="author_display_name"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'AI Name'}
                placeholder={'AI Assistant'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                infoTooltip={{
                  side: 'right',
                  description: 'AI name that will be visible to customers.',
                }}
              />
            )}
          />
        </div>
        <div className={'mt-2.5'}>
          <Controller
            control={control}
            name="welcome_message"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'AI Welcome message'}
                placeholder={'Hello! How can I help you?'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                infoTooltip={{
                  side: 'right',
                  description: (
                    <>
                      The first message AI will send <br /> once a chat widget is opened.
                    </>
                  ),
                }}
              />
            )}
          />
        </div>
      </form>
    </LoaderWrapper>
  );
};
